import { HttpContextToken } from '@angular/common/http';

export enum EScreenLockEntityType {
  dashboard = 'dashboard',
  slideshow = 'slideshow',
}

/** Which screen the lock is guarding; needed so the API knows what PIN to verify against. */
export interface IScreenLockTarget {
  entityType: EScreenLockEntityType;
  entityId: number;
  /** Shown as the keypad heading so the operator can see which screen is asking. */
  entityName?: string;
}

export interface IScreenLockConfig {
  /** Idle seconds (no mouse move / touch / key) before the screen locks itself. `0` disables auto lock. */
  idleTimeoutSeconds: number;
  /** Idle seconds before an open keypad closes itself and the screen falls back to the locked state. */
  keypadIdleTimeoutSeconds: number;
}

/** Lock resolved by the API on `GET dashboards/:id` / `GET slideshows/:id`; `null` means no lock applies. */
export interface IResolvedScreenLock {
  durationSeconds: number;
  sourceType: EScreenLockEntityType;
}

export interface IScreenLockVerifyResult {
  isValid: boolean;
  /** API-minted proof the PIN was entered; writes carry it back in {@link SCREEN_LOCK_TOKEN_HEADER}. */
  unlockToken?: string;
  expiresInSeconds?: number;
}

export const SCREEN_LOCK_TOKEN_HEADER = 'x-screen-lock-token';

/**
 * Mirrors `MAX_TOKENS_PER_REQUEST` in the API's `screen-lock-token.service.ts`, which reads only the first
 * this many tokens of the header. Sending more is not just waste: the API drops the surplus silently, so a
 * client that keeps the oldest tokens at the front loses the unlock it just obtained. Keep the two in step.
 */
export const SCREEN_LOCK_MAX_TOKENS_PER_REQUEST = 10;

/** `423 Locked` for a write against a PIN-protected screen. Not 403: the interceptor routes 403 to the forbidden page, but this is recoverable via the PIN. */
export const SCREEN_LOCK_UNLOCK_REQUIRED_STATUS = 423;

/**
 * Marks the write `screenLockInterceptor` replayed after an unlock. Lives here rather than in either
 * interceptor so `ApiInterceptor` can read it without importing the lock's interceptor back.
 */
export const SCREEN_LOCK_RETRIED = new HttpContextToken<boolean>(() => false);

/** One screen the API refused a write to, as named in a 423. */
export interface IScreenLockProtectedEntity {
  entityId: number;
  entityName: string;
}

/** The `contexts` block the API attaches to a 423, naming what still has to be unlocked. */
export interface IScreenLockUnlockRequirement {
  entityType: EScreenLockEntityType;
  lockedEntities: IScreenLockProtectedEntity[];
}

export const SCREEN_LOCK_DEFAULT_CONFIG: IScreenLockConfig = {
  idleTimeoutSeconds: 30,
  keypadIdleTimeoutSeconds: 20,
};
